import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useTranslation } from 'react-i18next'
import { FieldArray, useFormikContext } from 'formik'

import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import CheckboxInput from '@/components/ui/inputs/CheckboxInput'
import TimePickerField from '@/components/formik/fields/TimePickerField'
import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { periodGenerator } from '../../AvailabilityForm'

dayjs.extend(duration)

type RecurrenceDay = {
  day: number
  periods: Array<{ open: string; close: string }>
}

type WeeklyScheduleBuilderProps = {
  fieldName?: string
}

export default function WeeklyScheduleBuilder({ fieldName = 'recurrenceDays' }: WeeklyScheduleBuilderProps) {
  const { i18n, t } = useTranslation()
  const isMobile = useIsMobile()
  const { values, errors, touched, setFieldValue } = useFormikContext<{
    recurrenceDays: RecurrenceDay[]
  }>()

  const recurrenceDays = _.get(values, fieldName) as RecurrenceDay[]

  return (
    <Stack spacing={1}>
      {_.range(7).map((dayIndex) => {
        const dayPeriods = recurrenceDays[dayIndex]?.periods || []
        const hasPeriods = dayPeriods.length > 0

        return (
          <FieldArray key={dayIndex} name={`${fieldName}[${dayIndex}].periods`}>
            {({ push, remove }) => (
              <Stack
                direction={isMobile ? 'column' : 'row'}
                alignItems={hasPeriods ? 'flex-start' : 'center'}
                spacing={1}
                sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box sx={{ minWidth: 100 }} data-testid={`availability-wizard-day-${dayIndex}`}>
                  <CheckboxInput
                    checked={hasPeriods}
                    label={_.capitalize(dayjs().locale(i18n.language).day(dayIndex).format('ddd'))}
                    onChange={(event) => {
                      if (event.target.checked) {
                        push(periodGenerator('09:00', '12:00'))
                      } else {
                        setFieldValue(`${fieldName}[${dayIndex}].periods`, [])
                      }
                    }}
                  />
                </Box>

                <Stack flex={4} spacing={1}>
                  {hasPeriods ? (
                    dayPeriods.map((_period, periodIndex) => {
                      const openError = _.get(errors, `${fieldName}[${dayIndex}].periods[${periodIndex}].open`)
                      const closeError = _.get(errors, `${fieldName}[${dayIndex}].periods[${periodIndex}].close`)
                      const openTouched = _.get(touched, `${fieldName}[${dayIndex}].periods[${periodIndex}].open`)
                      const closeTouched = _.get(touched, `${fieldName}[${dayIndex}].periods[${periodIndex}].close`)

                      return (
                        <Stack key={`${dayIndex}-period-${periodIndex}`} spacing={0.5} sx={{ maxWidth: 300 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <TimePickerField
                              disableHelperText
                              fullWidth
                              name={`${fieldName}[${dayIndex}].periods[${periodIndex}].open`}
                            />
                            <TimePickerField
                              disableHelperText
                              fullWidth
                              name={`${fieldName}[${dayIndex}].periods[${periodIndex}].close`}
                            />
                            <Tooltip title={t('common:button.delete.tooltip')}>
                              <IconButton onClick={() => remove(periodIndex)} size="small">
                                <DeleteRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          {((openTouched && !!openError) || (closeTouched && !!closeError)) && (
                            <FormHelperText error sx={{ position: 'unset' }}>
                              <ErrorMessage error={openError || closeError} />
                            </FormHelperText>
                          )}
                        </Stack>
                      )
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: '37px' }}>
                      {t('page.createWaitingRoom.createWaitingRoomForm.infoSection.openingHours.message.noPeriod')}
                    </Typography>
                  )}
                </Stack>

                <Tooltip title={t('common:button.add.tooltip')}>
                  <IconButton
                    size="small"
                    data-testid={dayIndex === 0 ? 'availability-wizard-add-period' : undefined}
                    onClick={() => {
                      const lastPeriod = _.last(dayPeriods)
                      if (lastPeriod) {
                        const [h, m] = lastPeriod.close.split(':')
                        const lastClose = dayjs.duration({ hours: Number(h), minutes: Number(m) })
                        const nextOpen = lastClose.clone().add(1, 'h').asMinutes() < 24 * 60
                          ? lastClose.clone().add(1, 'h')
                          : lastClose.clone().subtract(23, 'h')
                        const nextOpenDur = dayjs.duration({ hours: nextOpen.hours(), minutes: nextOpen.minutes() })
                        const nextClose = nextOpenDur.clone().add(1, 'h').asMinutes() < 24 * 60
                          ? nextOpenDur.clone().add(1, 'h')
                          : nextOpenDur.clone().subtract(23, 'h')
                        push(periodGenerator(nextOpen.format('HH:mm'), nextClose.format('HH:mm')))
                      } else {
                        push(periodGenerator('09:00', '12:00'))
                      }
                    }}
                  >
                    <AddRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </FieldArray>
        )
      })}
    </Stack>
  )
}
